/**
 * One monochrome icon set, shared by the React UI and the floating window.
 *
 * The floating window is a separate document built with plain DOM, so icons are
 * stored as raw SVG-body strings that either side can wrap. Everything is drawn
 * with `currentColor`, so an icon simply inherits the colour of its button.
 */

export const ICONS = {
  flower:
    '<circle cx="12" cy="7" r="3.1"/><circle cx="17" cy="12" r="3.1"/><circle cx="12" cy="17" r="3.1"/><circle cx="7" cy="12" r="3.1"/><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/>',
  monitor:
    '<rect x="2.5" y="4" width="19" height="13" rx="2"/><path d="M9 21h6"/><path d="M12 17v4"/>',
  record: '<circle cx="12" cy="12" r="6" fill="currentColor" stroke="none"/>',
  play: '<path d="M8 5.2v13.6L19 12z" fill="currentColor" stroke="none"/>',
  pause:
    '<rect x="7" y="5" width="3.6" height="14" rx="1.2" fill="currentColor" stroke="none"/><rect x="13.4" y="5" width="3.6" height="14" rx="1.2" fill="currentColor" stroke="none"/>',
  stop: '<rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" stroke="none"/>',
  mic:
    '<path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3z"/><path d="M5 11a7 7 0 0 0 14 0"/><path d="M12 18v3"/>',
  micOff:
    '<path d="M9 9.4V12a3 3 0 0 0 5.1 2.1"/><path d="M15 10.6V6a3 3 0 0 0-5.7-1.3"/><path d="M5 11a7 7 0 0 0 10.9 5.8"/><path d="M19 11a7 7 0 0 1-.5 2.6"/><path d="M12 18v3"/><path d="M4 4l16 16"/>',
  face: '<circle cx="12" cy="8.5" r="4"/><path d="M4.5 20.5a7.5 7.5 0 0 1 15 0"/>',
  faceOff:
    '<path d="M9.2 5.7a4 4 0 0 1 5.3 5.3"/><path d="M10.5 12.4a4 4 0 0 1-1.2-1.2"/><path d="M6.2 19.5a6 6 0 0 1 5.8-4"/><path d="M15.4 15.9a7.5 7.5 0 0 1 2.6 3.6"/><path d="M4 4l16 16"/>',
  scissors:
    '<circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M20 4L8.1 15.9"/><path d="M14.5 14.5L20 20"/><path d="M8.1 8.1L12 12"/>',
  trash:
    '<path d="M4 7h16"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M6.5 7l.8 12.1A2 2 0 0 0 9.3 21h5.4a2 2 0 0 0 2-1.9L17.5 7"/><path d="M9.5 7V5.2A2 2 0 0 1 11.5 3h1a2 2 0 0 1 2 2.2V7"/>',
  download: '<path d="M12 3.5v11.5"/><path d="M7.5 10.5L12 15l4.5-4.5"/><path d="M4.5 20h15"/>',
  copy:
    '<rect x="9" y="9" width="11.5" height="11.5" rx="2"/><path d="M5.5 15.5A2 2 0 0 1 3.5 13.5v-8a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2"/>',
  check: '<path d="M4.5 12.5l5 5 10-11"/>',
  external:
    '<path d="M14 4h6v6"/><path d="M20 4l-8.5 8.5"/><path d="M18 14.5V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3.5"/>',
  expand:
    '<path d="M4 9V5.5A1.5 1.5 0 0 1 5.5 4H9"/><path d="M20 15v3.5a1.5 1.5 0 0 1-1.5 1.5H15"/><path d="M15 4h3.5A1.5 1.5 0 0 1 20 5.5V9"/><path d="M9 20H5.5A1.5 1.5 0 0 1 4 18.5V15"/>',
  arrowLeft: '<path d="M19.5 12h-15"/><path d="M10.5 5.5L4 12l6.5 6.5"/>',
  arrowRight: '<path d="M4.5 12h15"/><path d="M13.5 5.5L20 12l-6.5 6.5"/>',
} as const

export type IconName = keyof typeof ICONS

/** Icon markup for plain-DOM contexts (the floating window). */
export function iconSvg(name: IconName, size = 16) {
  return (
    `<svg class="ico" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" ` +
    `stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" ` +
    `aria-hidden="true">${ICONS[name]}</svg>`
  )
}
